import React, { Component } from 'react'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { toJS } from 'mobx'
import { inject } from 'mobx-react'
import { withRouter } from 'react-router'
import Button from '@components/Button'
import { Formik, Form, Field } from 'formik'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import RichTextEditor from '@components/Form/RichTextEditor'
import BigNumber from 'bignumber.js'
import Input from '@components/Form/Input'
import FieldPair from '@components/Form/FieldPair'
import DatePicker from '@components/Form/DatePicker'
import Checkbox from '@components/Form/Checkbox'

import { accountApi, employeeApi, clientContactApi, parentAccountApi, projectApi } from '@atheneum/api'

import ContractCoverageItems from './ContractCoverageItems'
import validationSchema from './validation'
import { StatusLabel } from '@components/StatusLabel'
import { formatNumber } from '@atheneum/api/dist/lib'

class ContractForm extends Component {
  handleParentAccountAutocomplete = input => {
    return parentAccountApi.typeaheadSearch(input)
  }

  handleClientContactAutocomplete = (input, form) => {
    const parentAccountId = get(form.values, 'parentAccount.id')

    if (parentAccountId) {
      return clientContactApi.typeaheadSearch(parentAccountId, input, true)
    }

    return Promise.resolve([])
  }

  handleEmployeeAutocomplete = input => {
    const { EMPLOYEE_POSITIONS } = this.props.staticData.CONST

    if (!input) {
      return Promise.resolve([])
    }

    const roles = [
      EMPLOYEE_POSITIONS.BD,
      EMPLOYEE_POSITIONS.ACCOUNTING,
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.EXECUTIVE,
      EMPLOYEE_POSITIONS.ADMIN,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ]

    return employeeApi.typeaheadSearch(roles, input)
  }

  handleAccountAutocomplete = form => {
    const parentAccountId = get(form.values, 'parentAccount.id')

    return parentAccountId
      ? accountApi.getAccountsByParentAccount(parentAccountId)
      : Promise.resolve([])
  }

  handleProjectAutocomplete = (input, form) => {
    const parentAccountId = get(form.values, 'parentAccount.id')

    return parentAccountId
      ? projectApi.typeaheadSearch(input, {
          parentAccount: parentAccountId,
          open: true,
          pending: true
        })
      : Promise.resolve([])
  }

  onParentAccountChange = (parentAccount, form) => {
    form.setFieldValue('parentAccount', parentAccount, false)
    form.setFieldTouched('parentAccount', true, true)
    form.setFieldValue('clientContact.id', null, false)
    form.setFieldValue('clientContact.fullName', null, false)
    form.setFieldValue('accounts', [{ id: null, companyName: null }])
    form.setFieldValue('projects', [{ id: null, projectName: null }])
  }

  onContractTypeChange = (contractTypeId, form) => {
    form.setFieldValue('contractTypeId', contractTypeId, false)
    form.setFieldValue('value', null, false)
    form.setFieldValue('unlimited', null, false)
    form.setFieldValue('rangeMin', null, false)
    form.setFieldValue('rangeMax', null, false)
    form.setFieldValue('creditAmount', null, false)
    form.setFieldValue('creditValue', null, false)
    form.setFieldValue('currencyId', null, false)
    form.setFieldValue('contractBalance', 0, false)
    form.setFieldTouched('contractTypeId', true, true)
  }

  onCoverageTypeChange = (id, form) => {
    form.setFieldValue('contractCoverageId', id, false)
    form.setFieldValue('accounts', [{ id: null, companyName: null }], false)
    form.setFieldValue('projects', [{ id: null, projectName: null }], false)
    form.setFieldTouched('contractCoverageId', true, true)
  }

  onUnlimitedChange = (unlimited, form) => {
    form.setFieldValue('unlimited', unlimited, false)
    form.setFieldValue('value', null, false)
    form.setFieldTouched('unlimited', true, true)
  }

  onValueChange = (value, form) => {
    const { contract, staticData } = this.props
    const balance = calculateContractBalance(
      parseFloat(value),
      form,
      contract,
      staticData
    )

    form.setFieldValue('value', value, false)
    form.setFieldValue('contractBalance', balance, false)
    form.setFieldTouched('value', true, true)
  }

  onCreditAmountChange = (creditAmount, form) => {
    const { contract, staticData } = this.props
    const balance = calculateContractBalance(
      parseFloat(creditAmount),
      form,
      contract,
      staticData
    )

    form.setFieldValue('creditAmount', creditAmount, false)
    form.setFieldValue('contractBalance', balance, false)
    form.setFieldTouched('creditAmount', true, true)
  }

  saveContract = contractData => {
    const { contract, contractStore, contractSearchStore } = this.props
    const { CONTRACT_TYPES, CONTRACT_COVERAGES } = this.props.staticData.CONST
    const { SPECIFIC_ACCOUNTS, SPECIFIC_PROJECTS } = CONTRACT_COVERAGES

    const paygNullFields = {
      creditValue: null,
      creditAmount: null
    }

    const nonPaygNullFields = {
      value: null,
      unlimited: null,
      rangeMin: null,
      rangeMax: null,
      feeOverchargeLimit: null,
    }

    let data = {
      ...omit(contractData, [
        'parentAccount',
        'clientContact',
        'employeeContact',
        'contractBalance'
      ]),
      parentAccountId: contractData.parentAccount.id,
      clientContactId: get(contractData, 'clientContact.id', null),
      employeeContactId: contractData.employeeContact.id
    }

    if (contractData.contractTypeId === CONTRACT_TYPES.PAYG) {
      data = { ...data, ...paygNullFields }
    } else {
      data = { ...data, ...nonPaygNullFields }
    }

    data.projects =
      data.contractCoverageId === SPECIFIC_PROJECTS
        ? data.projects.filter(({ id }) => id != null).map(({ id }) => ({ id }))
        : []
    data.accounts =
      data.contractCoverageId === SPECIFIC_ACCOUNTS
        ? data.accounts.filter(({ id }) => id != null).map(({ id }) => ({ id }))
        : []

    if (this.props.contract) {
      return contractStore.update(contract, data)
    }

    return contractSearchStore.create(data)
  }

  initForm = () => {
    const { contract, staticData } = this.props
    const { CONTRACT_TYPE_DETAILS } = staticData.CONST

    return contract
      ? {
          ...pick(toJS(contract), [
            'parentAccount.id',
            'parentAccount.parentAccountName',
            'clientContact.id',
            'clientContact.fullName',
            'clientContractParty',
            'atheneumOfficeId',
            'employeeContact.id',
            'employeeContact.fullName',
            'contractCoverageId',
            'accounts',
            'projects',
            'contractTypeId',
            'contractTypeDetailId',
            'startDate',
            'endDate',
            'rangeMin',
            'rangeMax',
            'feeOverchargeLimit',
            'value',
            'unlimited',
            'creditAmount',
            'creditValue',
            'marginPredictionInFeeValue',
            'currencyId',
            'invoicingInstructions',
            'specialConditions',
            'additionalComment',
            'signed'
          ]),
          contractBalance: contract.unlimited ? 0 : contract.contractBalance
        }
      : {
          parentAccount: {
            id: null,
            parentAccountName: null
          },
          clientContact: {
            id: null,
            fullName: null
          },
          clientContractParty: '',
          atheneumOfficeId: null,
          employeeContact: {
            id: null,
            fullName: null
          },
          contractCoverageId: null,
          accounts: [{ id: null, companyName: null }],
          projects: [{ id: null, projectName: null }],
          contractTypeId: null,
          contractTypeDetailId: CONTRACT_TYPE_DETAILS.FINAL,
          startDate: null,
          endDate: null,
          rangeMin: null,
          rangeMax: null,
          feeOverchargeLimit: null,
          value: null,
          unlimited: null,
          marginPredictionInFeeValue: false,
          creditAmount: null,
          creditValue: null,
          currencyId: null,
          invoicingInstructions: null,
          specialConditions: null,
          additionalComment: null,
          signed: false,
          contractBalance: 0,
          calculatedTotal: 0
        }
  }

  render () {
    const {
      contract,
      contractStore,
      title,
      staticData,
      atheneumOfficeStore,
      history,
      feeCount
    } = this.props
    const formTitle = `${title} ${contract ? contract.contractReference : ''}`

    const { CONTRACT_TYPES } = staticData.CONST
    const MUNICH = 10 // considered same as Berlin, agreed to be excluded
    const atheneumOffices = atheneumOfficeStore.atheneumOffices
      .map(office => {
        return {
          id: office.id,
          entityName: office.entityName
        }
      })
      .filter(office => office.id !== MUNICH)

    return (
      <div className='expert-form'>
        <h1 className='contract-form__title'>
          <span className='m-r-md'>{formTitle}</span>
          {contract ? (
            <StatusLabel
              statusId={contract.contractStatusId}
              statusesType='contractStatuses'
            />
          ) : null}
        </h1>
        <Formik
          enableReinitialize
          initialValues={this.initForm()}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            this.saveContract(values)
              .then(savedContract => {
                contract
                  ? contractStore.get(contract.id)
                  : history.push(`/contract/${savedContract.id}`)
                this.props.modalStore.close()
              })
              .catch(({ message }) => {
                this.error = message
                actions.setSubmitting(false)
              })
          }}
          render={form => {
            return (
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <Field
                    name='parentAccount'
                    label='Parent account'
                    component={Autocomplete}
                    loadOptions={this.handleParentAccountAutocomplete}
                    disabled={!!contract}
                    onChange={value => this.onParentAccountChange(value, form)}
                    itemToString={item => item && item.parentAccountName}
                  />
                  <Field
                    name='clientContact'
                    label='Client key contact'
                    component={Autocomplete}
                    loadOptions={value =>
                      this.handleClientContactAutocomplete(value, form)
                    }
                    itemToString={item => item && item.fullName}
                  />
                  <Field
                    name='clientContractParty'
                    label='Client contract party'
                    tooltip='Client legal name'
                    component={Input}
                  />
                  <Field
                    name='atheneumOfficeId'
                    label='Atheneum contract party'
                    itemToString={item => item && item.entityName}
                    options={atheneumOffices}
                    component={Dropdown}
                  />
                  <Field
                    name='employeeContact'
                    label='Signed by'
                    component={Autocomplete}
                    loadOptions={value =>
                      this.handleEmployeeAutocomplete(value, form)
                    }
                    itemToString={item => item && item.fullName}
                  />
                  <Field
                    disabled={contract && feeCount > 0}
                    name='contractCoverageId'
                    label='Contract coverage'
                    component={Dropdown}
                    options={staticData.contractCoverages}
                    itemToString={item => item && item.name}
                    onChange={item => this.onCoverageTypeChange(item.id, form)}
                  />
                  {this.renderCoverageItems(form)}
                  {this.renderContractTypeFields(form)}
                  {[
                    CONTRACT_TYPES.SUBSCRIPTION,
                    CONTRACT_TYPES.PACKAGE
                  ].includes(form.values.contractTypeId) && (
                    <div className='expert-form__input-group'>
                      <Field
                        name='marginPredictionInFeeValue'
                        label='Calculate margin predictions on epl using fee values'
                        component={Checkbox}
                      />
                    </div>
                  )}
                  <div className='expert-form__input-group'>
                    <Field name='signed' label='Signed' component={Checkbox} />
                  </div>
                </div>
                {this.renderContractParametersSection(form)}
                <div className='expert-form__section'>
                  <h2 className='expert-form__subtitle'>More information</h2>
                  <Field
                    name='invoicingInstructions'
                    label='Invoicing instructions'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='specialConditions'
                    label='Special conditions'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <Field
                    name='additionalComment'
                    label='Additional comment'
                    placeholder=''
                    toolbarRight
                    component={RichTextEditor}
                  />
                </div>
                <div className='expert-form__section expert-form__section--buttons'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={this.props.modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )
          }}
        />
      </div>
    )
  }

  renderContractTypeFields = form => {
    const { contract, staticData } = this.props
    const { contractTypes, contractTypeDetails } = staticData

    return (
      <div className='expert-form__input-group'>
        <label className='expert-form__label'>Contract type</label>
        <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
          <Field
            disabled={contract}
            name='contractTypeId'
            options={contractTypes}
            itemToString={item => item && item.name}
            component={Dropdown}
            onChange={type => this.onContractTypeChange(type.id, form)}
          />
          <Field
            name='contractTypeDetailId'
            options={contractTypeDetails}
            itemToString={item => item && item.name}
            component={Dropdown}
          />
        </div>
      </div>
    )
  }

  renderCoverageItems = form => {
    const { contractCoverageId } = form.values
    const { contract, staticData, feeCount } = this.props
    const { CONTRACT_COVERAGES } = staticData.CONST

    if (
      !contractCoverageId &&
      contractCoverageId === CONTRACT_COVERAGES.GLOBAL
    ) {
      return null
    }

    if (contractCoverageId === CONTRACT_COVERAGES.SPECIFIC_ACCOUNTS) {
      return (
        <ContractCoverageItems
          contract={contract}
          disabled={contract && feeCount > 0}
          form={form}
          name='accounts'
          autocompleteProps={{
            loadOptions: () => this.handleAccountAutocomplete(form),
            itemToString: item => item && item.companyName
          }}
          repeatableActionProps={{
            initialObject: { id: null, companyName: null }
          }}
        />
      )
    }

    if (contractCoverageId === CONTRACT_COVERAGES.SPECIFIC_PROJECTS) {
      return (
        <ContractCoverageItems
          contract={contract}
          disabled={contract && feeCount > 0}
          form={form}
          name='projects'
          autocompleteProps={{
            loadOptions: input => this.handleProjectAutocomplete(input, form),
            itemToString: item => item && item.projectName
          }}
          repeatableActionProps={{
            initialObject: { id: null, projectName: null }
          }}
        />
      )
    }
  }

  renderContractParametersSection (form) {
    const { staticData, contract, feeCount } = this.props
    const { CONTRACT_TYPES } = staticData.CONST
    const { currencies } = staticData.names
    const isPAYG = form.values.contractTypeId === CONTRACT_TYPES.PAYG

    if (!form.values.contractTypeId) {
      return null
    }

    if (isPAYG) {
      return (
        <div className='expert-form__section'>
          <h2 className='expert-form__subtitle'>PAYG Contract parameters</h2>
          <DateRange form={form} disableStartDate={contract && feeCount > 0} />
          <PriceRange {...this.props} form={form} />
          <TotalValue
            {...this.props}
            onUnlimitedChange={this.onUnlimitedChange}
            onValueChange={this.onValueChange}
            form={form}
          />
          {contract ? (
            <div>
              <div className='expert-form__input-group'>
                <label className='expert-form__label'>Remaining balance</label>
                <div className='expert-form__value-wrapper'>
                  {form.values.unlimited
                    ? 'N/A'
                    : formatNumber(form.values.contractBalance) +
                      ' ' +
                      currencies[contract.currencyId]}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )
    }

    return (
      <div className='expert-form__section'>
        <h2 className='expert-form__subtitle'>Non-PAYG Contract parameters</h2>
        <DateRange form={form} disableStartDate={contract && feeCount > 0} />
        <Field
          className='expert-form__short-field'
          name='creditAmount'
          label='Credits'
          component={Input}
          onChange={e => this.onCreditAmountChange(e.target.value, form)}
        />
        <CreditValue
          disabled={contract && feeCount > 0}
          {...this.props}
          form={form}
        />
        <TotalValue {...this.props} form={form} readOnly />
        {contract ? (
          <div>
            <div className='expert-form__input-group'>
              <label className='expert-form__label'>Remaining balance</label>
              <div className='expert-form__value-wrapper'>
                {formatNumber(form.values.contractBalance)} credits
              </div>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false
  }

  static propTypes = {
    contract: PropTypes.object,
    contractStore: PropTypes.object,
    contractSearchStore: PropTypes.object,
    atheneumOfficeStore: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    history: PropTypes.object,
    title: PropTypes.string,
    feeCount: PropTypes.number
  }
}

function DateRange (props) {
  const { form, disableStartDate } = props
  return (
    <FieldPair
      separator
      form={form}
      label='Start – End date'
      fieldAProps={{
        disabled: disableStartDate,
        noError: true,
        dateOnly: true,
        name: 'startDate',
        date: form.values.startDate,
        maxDate: form.values.endDate,
        placeholder: 'from'
      }}
      fieldBProps={{
        noError: true,
        dateOnly: true,
        name: 'endDate',
        date: form.values.endDate,
        minDate: form.values.startDate,
        placeholder: 'to'
      }}
      componentA={DatePicker}
      componentB={DatePicker}
    />
  )
}

DateRange.propTypes = {
  form: PropTypes.object,
  disableStartDate: PropTypes.bool
}

function PriceRange (props) {
  return (
    <FieldPair
      form={props.form}
      separator
      label='Price range'
      fieldAProps={{
        name: 'rangeMin',
        placeholder: 'price from'
      }}
      fieldBProps={{
        name: 'rangeMax',
        placeholder: 'price to'
      }}
      componentA={Input}
      componentB={Input}
    />
  )
}

PriceRange.propTypes = {
  form: PropTypes.object
}

function TotalValue (props) {
  const { staticData, readOnly, form, onUnlimitedChange, onValueChange } = props
  const { currencies, names } = staticData
  const currencyNames = names.currencies

  const {
    value,
    currencyId,
    creditAmount,
    creditValue,
    unlimited
  } = form.values

  const currencyName = currencyNames[currencyId]

  const showTotalValue =
    (unlimited || value || (creditAmount && creditValue)) && currencyId

  let totalValue

  if (unlimited) {
    totalValue = 'Unlimited'
  } else {
    totalValue = formatNumber(
      Number(value) || Number(creditAmount) * Number(creditValue)
    )
  }

  if (readOnly) {
    return (
      <div className='expert-form__input-group'>
        <label className='expert-form__label'>Total value</label>
        <div className='expert-form__value-wrapper'>
          {showTotalValue ? `${totalValue} ${currencyName}` : '-'}
        </div>
      </div>
    )
  }

  return (
    <div className='expert-form__input-group total-value__group'>
      <label className='expert-form__label total-value__group-label'>
        Total value
      </label>
      <FieldPair
        form={form}
        fieldAProps={{
          disabled: unlimited,
          name: 'value',
          placeholder: 'value',
          onChange: e => onValueChange(e.target.value, form)
        }}
        fieldBProps={{
          noError: true,
          name: 'currencyId',
          placeholder: 'currency',
          options: currencies,
          itemToString: item => item && item.name
        }}
        componentA={Input}
        componentB={Dropdown}
      />
      <div>
        <Field
          form={form}
          name='unlimited'
          label='Unlimited'
          component={Checkbox}
          onChange={e => onUnlimitedChange(e.target.checked, form)}
        />
      </div>
    </div>
  )
}

TotalValue.propTypes = {
  staticData: PropTypes.object,
  readOnly: PropTypes.bool,
  form: PropTypes.object,
  onUnlimitedChange: PropTypes.func,
  onValueChange: PropTypes.func
}

function CreditValue (props) {
  const { disabled, staticData, form } = props
  const { currencies } = staticData

  return (
    <FieldPair
      form={form}
      label='Credits value'
      fieldAProps={{
        disabled,
        name: 'creditValue',
        placeholder: 'value'
      }}
      fieldBProps={{
        disabled,
        noError: true,
        name: 'currencyId',
        options: currencies,
        itemToString: item => item && item.name
      }}
      componentA={Input}
      componentB={Dropdown}
    />
  )
}

CreditValue.propTypes = {
  staticData: PropTypes.object,
  disabled: PropTypes.bool,
  form: PropTypes.object
}

function calculateContractBalance (amount, form, contract, staticData) {
  const { CONTRACT_TYPES } = staticData.CONST
  const { contractTypeId } = form.values

  if (contract && !form.values.unlimited) {
    const amountField =
      contractTypeId === CONTRACT_TYPES.PAYG ? 'value' : 'creditAmount'

    let initBalance = 0
    let initAmount = 0

    if (form.values.contractTypeId === contract.contractTypeId) {
      initBalance = contract.contractBalance || 0
      initAmount = contract[amountField] || 0
    }

    const amountDiff = BigNumber(amount || 0).minus(initAmount)

    return BigNumber(initBalance)
      .plus(amountDiff)
      .toNumber()
  }

  return 0
}

export default withRouter(
  inject('staticData', 'modalStore', 'atheneumOfficeStore')(ContractForm)
)
